import { z } from "zod";

import { AgentIpcEnvelopeBaseSchema } from "../../envelope/envelope-base.schema";
import { AGENT_IPC_KIND } from "../kinds.constant";

/**
 * One SDK server declared on the session, with the connection state the SDK
 * reported for it.
 *
 * Both fields are optional and both swallow a wrong type, for the same reason
 * the top level does: this element sits inside the frame carrying `sessionId`,
 * and a strict nested field refuses that whole frame rather than just itself.
 * `status` is a bare string on the SDK's own type — a connection state is not
 * statically knowable — so nothing here can be pinned to a value set either.
 */
const SystemInitMcpServerSchema = z.object({
  name: z.string().optional().catch(undefined),
  status: z.string().optional().catch(undefined)
});

/**
 * One plugin the session loaded, as the SDK names it.
 *
 * Tolerant for the reason above, and with cause: the SDK documents `version` as
 * emitted verbatim from a plugin author's own manifest, so its type is outside
 * anyone's control here.
 *
 * The SDK also reports the plugin's filesystem `path` inside the sandbox. It is
 * deliberately not carried: this event reaches a browser and a trace row, the
 * name and version answer every reason the plugins are carried at all, and an
 * internal path answers none of them.
 */
const SystemInitPluginSchema = z.object({
  name: z.string().optional().catch(undefined),
  version: z.string().optional().catch(undefined)
});

/**
 * The SDK's `system/init` message: the session id it confirmed, and what that
 * session booted with.
 *
 * **Emitted once per TURN, not once per session.** The SDK's own doc frames it
 * as step one of the agent loop, which reads as a handshake; the measured
 * behaviour is a frame per turn — one run in the research corpus carries
 * fifteen, one per Monitor event, and others follow compaction, a conversation
 * reset and a local slash command. A consumer that keys "new session" off this
 * event tears its state down on every background-task completion. Each frame is
 * a complete snapshot of session config, so the right reading is last-wins: a
 * missed frame is recovered from the next one.
 *
 * **`sessionId` is the only strict field, and that is load-bearing.** A sandbox
 * boots its own snapshot and outlives the deploy that replaced it, so a runner
 * baked before these fields existed goes on emitting the session id alone, and
 * one baked after this build may emit a value this build has not seen. The
 * shapes are strict, so any config field that could refuse would refuse the
 * whole frame — and this is the frame carrying `sessionId`, which
 * `stream-start` and every resume path read.
 *
 * So every config field, at both levels, is optional AND swallows a wrong type.
 * The rule is the whole config half rather than the fields that happened to
 * need it: one field added later without the same tolerance reopens the hazard,
 * and it would take the session id down with it.
 *
 * **The SDK's own enumerations travel as open strings** rather than as unions,
 * on the same argument. A sandbox boots its own snapshot and can emit a member
 * this build's pinned SDK has not declared — `ApiKeySource` has already gained
 * members across versions — and a union would refuse the whole frame for a
 * field nothing branches on, taking the session id with it. Pinning these to
 * the values one SDK version happens to declare is the change this paragraph
 * exists to stop.
 *
 * **This fact stops at the wire, deliberately** — a slice owes an explicit
 * persistence answer either way. It is not folded into the chat kernel: it is
 * per-turn configuration rather than content, nothing in a transcript renders
 * it, and folding it would persist a row per turn for a fact no reader draws.
 *
 * What is durable instead is a trace row per turn that reported configuration.
 * A reopened conversation answers "which model ran, which tools existed" by
 * reading those rows in order, so a roster that changed mid-session is legible
 * rather than overwritten. On the browser wire every change travels live.
 *
 * The CLI version and the capability list are deliberately absent — they are
 * NEX-4674's half of this message and are not duplicated here. Cold-start boot
 * telemetry likewise rides its own `runner_metrics` event.
 */
export const SystemInitSchema = AgentIpcEnvelopeBaseSchema.extend({
  kind: z.literal(AGENT_IPC_KIND.SystemInit),
  sessionId: z.string().min(1),
  /**
   * The tool names available to the session. Varies with `permissionMode`
   * within one session, which is why a reader takes the latest frame rather
   * than caching the first. `Task` here is the internal name for the `Agent`
   * tool — the model emits `Agent` and never sees `Task`.
   */
  tools: z.array(z.string()).optional().catch(undefined),
  /** The agent types the session can dispatch. */
  agents: z.array(z.string()).optional().catch(undefined),
  /** The skills loaded for the session. */
  skills: z.array(z.string()).optional().catch(undefined),
  /** The slash commands the session resolved. */
  slashCommands: z.array(z.string()).optional().catch(undefined),
  /** The MCP servers declared on the session, with their reported state. */
  mcpServers: z.array(SystemInitMcpServerSchema).optional().catch(undefined),
  /** The model this turn ran against. */
  model: z.string().optional().catch(undefined),
  /**
   * The permission mode in force. An open string rather than the SDK's union:
   * a mode this build has not seen must not refuse the frame carrying the
   * session id.
   */
  permissionMode: z.string().optional().catch(undefined),
  /** The output style the session booted with. */
  outputStyle: z.string().optional().catch(undefined),
  /** The plugins the session loaded. */
  plugins: z.array(SystemInitPluginSchema).optional().catch(undefined),
  /** The API betas the session enabled. */
  betas: z.array(z.string()).optional().catch(undefined),
  /** Where the SDK resolved its credential from. Open string — see above. */
  apiKeySource: z.string().optional().catch(undefined),
  /** Whether fast mode is on for this session, as the SDK reports it. */
  fastModeState: z.string().optional().catch(undefined)
});

export type SystemInitEvent = z.infer<typeof SystemInitSchema>;
export type SystemInitMcpServer = z.infer<typeof SystemInitMcpServerSchema>;
export type SystemInitPlugin = z.infer<typeof SystemInitPluginSchema>;

/**
 * The config half of the init frame — everything the session booted with, with
 * the envelope and the session id taken off. What a host records against the
 * trace, and what the browser-facing `session-config` member carries.
 */
export type SystemInitConfig = Omit<SystemInitEvent, "protocolVersion" | "kind" | "sessionId">;
